import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import {
  Divider,
  Flex,
  Image,
  Input,
  message,
  Radio,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { FC, useRef, useState } from 'react';
import {
  Controller,
  get,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { formatSelectOptions } from 'src/app/utils/formatSelectOptions';

import { ProductType } from '../../types/types';
import { BenefitsList } from '../BenefitsList';
import { FormItem } from '../FormItem';
import { MarketplaceImageUpload } from '../MarketplaceImageUpload';
import { ReviewsList } from '../ReviewsList';
import { Section } from '../Section';

const { TextArea } = Input;
const { Dragger } = Upload;
const { Text } = Typography;

const MAX_CAROUSEL_IMAGES = 10;

const levelOptions = formatSelectOptions(
  [
    { id: 'iniciante', name: 'Iniciante' },
    { id: 'intermediario', name: 'Intermediário' },
    { id: 'avancado', name: 'Avançado' },
  ],
  {
    value: 'id',
    label: 'name',
  }
);

export const LandingPageStep: FC = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const mediaType = useWatch({ control, name: 'content.mediaType' });
  const productType = useWatch({ control, name: 'creation.productType' });
  const creationTitle = useWatch({ control, name: 'creation.title' });
  const isCombo = productType === ProductType.Combo;
  const [previewVisible, setPreviewVisible] = useState<string | null>(null);
  const pendingCarouselFilesRef = useRef<UploadFile[]>([]);

  const {
    fields: benefitFields,
    append: appendBenefit,
    remove: removeBenefit,
  } = useFieldArray({ control, name: 'content.benefits' as any });

  const {
    fields: reviewFields,
    append: appendReview,
    remove: removeReview,
  } = useFieldArray({ control, name: 'content.reviews' });

  return (
    <Space direction='vertical' size={32} style={{ width: '100%' }}>
      <Section
        title='Informações do Item'
        subtitle='Informações principais exibidas no topo da landing page.'
        type='primary'
      >
        <FormItem
          label='Nome do Item'
          control={control}
          name='content.title'
          renderItem={({ field }) => (
            <Input
              {...field}
              value={creationTitle}
              readOnly
              variant='filled'
              style={{ cursor: 'default', color: 'rgba(255,255,255,0.45)' }}
            />
          )}
        />

        {!isCombo && (
          <Flex gap={16}>
            <Flex vertical style={{ flex: 1 }}>
              <FormItem
                label='Duração'
                required
                control={control}
                name='content.durationTime'
                renderItem={({ field }) => (
                  <Input
                    {...field}
                    placeholder='Ex.: 14 minutos'
                    status={
                      get(errors, 'content.durationTime') ? 'error' : undefined
                    }
                  />
                )}
              />
            </Flex>
            <Flex vertical style={{ flex: 1 }}>
              <FormItem
                label='Nível'
                control={control}
                required
                name='content.level'
                renderItem={({ field }) => (
                  <Select
                    {...field}
                    placeholder='Selecionar'
                    options={levelOptions}
                    style={{ width: '100%' }}
                    status={get(errors, 'content.level') ? 'error' : undefined}
                  />
                )}
              />
            </Flex>
            <Flex vertical style={{ flex: 1 }}>
              <FormItem
                label='Objetivo'
                control={control}
                required
                name='content.objective'
                renderItem={({ field }) => (
                  <Input
                    {...field}
                    placeholder='Ex.: Perder Peso'
                    status={
                      get(errors, 'content.objective') ? 'error' : undefined
                    }
                  />
                )}
              />
            </Flex>
          </Flex>
        )}
      </Section>

      <Divider />

      <Section
        title='Teaser & Capa'
        subtitle='Imagem de capa ou vídeo de apresentação do item.'
        type='primary'
      >
        <FormItem
          control={control}
          name='content.mediaType'
          renderItem={({ field }) => (
            <Radio.Group {...field}>
              <Radio value='video'>ID Spala</Radio>
              <Radio value='image'>Imagem (.jpg, .png ou .gif)</Radio>
            </Radio.Group>
          )}
        />

        {mediaType === 'video' ? (
          <FormItem
            label='ID do Vídeo (Spala)'
            required
            control={control}
            name='content.teaser'
            renderItem={({ field }) => (
              <Input
                {...field}
                placeholder='Digite o ID do vídeo'
                status={get(errors, 'content.teaser') ? 'error' : undefined}
              />
            )}
          />
        ) : (
          <MarketplaceImageUpload
            title='Imagem Principal'
            required
            fieldName='content.mainImage'
            maxDimensions={{ width: 1125, height: 634 }}
          />
        )}
      </Section>

      <Divider />

      <Section title='Descrição' type='primary'>
        <FormItem
          label='Título Principal'
          required
          control={control}
          name='content.mainTitle'
          renderItem={({ field }) => (
            <Input
              {...field}
              placeholder='Tenha acesso'
              maxLength={140}
              showCount
              status={get(errors, 'content.mainTitle') ? 'error' : undefined}
            />
          )}
        />

        <FormItem
          label='Descrição Principal'
          required
          control={control}
          name='content.description'
          renderItem={({ field }) => (
            <TextArea
              {...field}
              placeholder='Descreva os benefícios e detalhes do programa...'
              rows={5}
              maxLength={500}
              showCount
              status={get(errors, 'content.description') ? 'error' : undefined}
            />
          )}
        />

        <Text type='secondary'>
          Esse título aparece em destaque abaixo da imagem ou vídeo.
        </Text>
      </Section>

      <Divider />

      <Section
        title='Tenha acesso'
        subtitle='Liste os benefícios e características do programa'
        type='primary'
      >
        <BenefitsList
          control={control}
          fields={benefitFields}
          append={appendBenefit}
          remove={removeBenefit}
        />
        {get(errors, 'content.benefits')?.message && (
          <Text type='danger'>{get(errors, 'content.benefits').message}</Text>
        )}
      </Section>

      <Divider />

      <Section
        title='Opinião de quem já testou'
        subtitle='Adicione avaliações de clientes'
        type='primary'
      >
        <FormItem
          label='Título da seção'
          control={control}
          name='content.reviewsSectionTitle'
          renderItem={({ field }) => (
            <Input
              {...field}
              placeholder='Opinião de quem já testou'
              status={
                get(errors, 'content.reviewsSectionTitle') ? 'error' : undefined
              }
            />
          )}
        />

        <ReviewsList
          control={control}
          fields={reviewFields}
          append={appendReview}
          remove={removeReview}
        />
        {get(errors, 'content.reviews')?.message && (
          <Text type='danger'>{get(errors, 'content.reviews').message}</Text>
        )}
      </Section>

      {isCombo && (
        <>
          <Divider />

          <Section
            title='Tudo o que você recebe'
            subtitle='Seção para inclusão das imagens do combo'
            type='primary'
          >
            <FormItem
              label='Quantidade de programas'
              required
              control={control}
              name='content.programLength'
              renderItem={({ field }) => (
                <>
                  <Input
                    {...field}
                    placeholder='Ex.: 190'
                    status={
                      get(errors, 'content.programLength') ? 'error' : undefined
                    }
                  />
                  <Text type='secondary' style={{ fontSize: 12, marginTop: 4 }}>
                    Exibido como &quot;190 Programas&quot; no preview.
                  </Text>
                </>
              )}
            />

            <div>
              <Flex
                justify='space-between'
                align='center'
                style={{ marginBottom: 8 }}
              >
                <Flex align='center' gap={4}>
                  <Text strong>Imagens do carrossel</Text>
                  <Text type='danger'>*</Text>
                </Flex>
                <Text type='secondary'>
                  <Controller
                    control={control}
                    name='content.programImages'
                    render={({ field }) => (
                      <span>
                        <span style={{ color: '#1668DC' }}>
                          {(field.value as UploadFile[] | undefined)?.length ||
                            0}
                        </span>{' '}
                        de {MAX_CAROUSEL_IMAGES} imagens
                      </span>
                    )}
                  />
                </Text>
              </Flex>

              <Controller
                control={control}
                name='content.programImages'
                render={({ field }) => {
                  const currentFiles =
                    (field.value as UploadFile[] | undefined) || [];

                  return (
                    <>
                      <Dragger
                        multiple
                        showUploadList={false}
                        fileList={currentFiles}
                        beforeUpload={(file, fileList) => {
                          const totalFiles =
                            currentFiles.length + fileList.length;
                          if (totalFiles > MAX_CAROUSEL_IMAGES) {
                            message.error(
                              `Máximo de ${MAX_CAROUSEL_IMAGES} imagens permitidas.`
                            );
                            return Upload.LIST_IGNORE;
                          }
                          const uploadFile: UploadFile = {
                            uid: `${Date.now()}-${file.name}`,
                            name: file.name,
                            status: 'done',
                            originFileObj: file,
                            thumbUrl: URL.createObjectURL(file),
                          };
                          pendingCarouselFilesRef.current.push(uploadFile);
                          if (
                            pendingCarouselFilesRef.current.length ===
                            fileList.length
                          ) {
                            field.onChange([
                              ...currentFiles,
                              ...pendingCarouselFilesRef.current,
                            ]);
                            pendingCarouselFilesRef.current = [];
                          }
                          return false;
                        }}
                        accept='image/png,image/jpeg,image/webp'
                        style={{
                          background: '#141414',
                          borderColor: '#424242',
                        }}
                      >
                        <p className='ant-upload-drag-icon'>
                          <InboxOutlined />
                        </p>
                        <p className='ant-upload-text'>
                          Clique para enviar ou arraste imagens aqui
                        </p>
                        <p className='ant-upload-hint'>360x186 pixels</p>
                      </Dragger>

                      {currentFiles.length > 0 && (
                        <Flex wrap='wrap' gap={8} style={{ marginTop: 12 }}>
                          {currentFiles.map(file => (
                            <Flex
                              key={file.uid}
                              align='center'
                              gap={8}
                              style={{
                                width: 'calc(20% - 7px)',
                                minWidth: 180,
                                padding: '8px 12px',
                                border: '1px solid #424242',
                                borderRadius: 8,
                              }}
                            >
                              {file.thumbUrl && (
                                <Image
                                  src={file.thumbUrl}
                                  alt={file.name}
                                  width={48}
                                  height={48}
                                  style={{ objectFit: 'cover', flexShrink: 0 }}
                                  preview={{
                                    visible: previewVisible === file.uid,
                                    onVisibleChange: vis => {
                                      if (vis) setPreviewVisible(file.uid);
                                      else setPreviewVisible(null);
                                    },
                                  }}
                                />
                              )}
                              <Text
                                ellipsis
                                style={{
                                  flex: 1,
                                  color: '#1668DC',
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  file.thumbUrl && setPreviewVisible(file.uid)
                                }
                              >
                                {file.name}
                              </Text>
                              <DeleteOutlined
                                onClick={() =>
                                  field.onChange(
                                    currentFiles.filter(f => f.uid !== file.uid)
                                  )
                                }
                                style={{
                                  flexShrink: 0,
                                  cursor: 'pointer',
                                  color: '#999',
                                }}
                              />
                            </Flex>
                          ))}
                        </Flex>
                      )}
                    </>
                  );
                }}
              />
            </div>
            {get(errors, 'content.programImages')?.message && (
              <Text type='danger'>
                {get(errors, 'content.programImages').message}
              </Text>
            )}
          </Section>
        </>
      )}

      {!isCombo && (
        <>
          <Divider />

          <Section
            title='Conheça quem vai te ajudar'
            subtitle='Informações exibidas na seção "Conheça quem vai te ajudar".'
            type='primary'
          >
            <Flex gap={24} align='stretch'>
              <div
                style={{
                  flex: '0 0 33%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <MarketplaceImageUpload
                  fieldName='content.instructor.image'
                  helperText='1184x700  pixels'
                  maxDimensions={{ width: 1184, height: 700 }}
                />
              </div>
              <Flex vertical gap={16} style={{ flex: 1 }}>
                <FormItem
                  label='Nome completo'
                  required
                  control={control}
                  name='content.instructor.name'
                  renderItem={({ field }) => (
                    <Input
                      {...field}
                      placeholder='Gabriela Cangussu'
                      status={
                        get(errors, 'content.instructor.name')
                          ? 'error'
                          : undefined
                      }
                    />
                  )}
                />

                <FormItem
                  label='Registro profissional'
                  required
                  control={control}
                  name='content.instructor.cref'
                  renderItem={({ field }) => (
                    <Input
                      {...field}
                      placeholder='CREF - 32501'
                      status={
                        get(errors, 'content.instructor.cref')
                          ? 'error'
                          : undefined
                      }
                    />
                  )}
                />

                <FormItem
                  label='Biografia'
                  control={control}
                  name='content.instructor.biography'
                  required
                  renderItem={({ field }) => (
                    <TextArea
                      {...field}
                      placeholder='Conhecida como a "Personal das Mamães Saradas", Gabriela Cangussu foi pioneira no Brasil em treinos para mães. Criadora do Mamãe Sarada, já impactou mais de 553.201 mulheres, ajudando-as a reconquistar confiança, autoestima e saúde em todas as fases da maternidade.'
                      rows={4}
                      status={
                        get(errors, 'content.instructor.biography')
                          ? 'error'
                          : undefined
                      }
                    />
                  )}
                />
              </Flex>
            </Flex>
          </Section>
        </>
      )}

      {isCombo && (
        <>
          <Divider />

          <Section
            title='CTA'
            subtitle='Botão de ação exibido na barra fixa inferior.'
            type='primary'
          >
            <FormItem
              label='Texto do botão'
              required
              control={control}
              name='content.buttonText'
              renderItem={({ field }) => (
                <Input
                  {...field}
                  placeholder='Ver planos...'
                  status={
                    get(errors, 'content.buttonText') ? 'error' : undefined
                  }
                />
              )}
            />
          </Section>
        </>
      )}
    </Space>
  );
};
